import { addDays, addMonths, addWeeks, addYears, startOfDay } from 'date-fns';

import { CamDateRange } from '../types';
import { overlaps, toDate } from './date';

type ParsedRule = {
  byDay: number[];
  count: number | null;
  freq: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY' | null;
  interval: number;
  until: Date | null;
};

const DAY_CODES: { [code: string]: number } = {
  FR: 5,
  MO: 1,
  SA: 6,
  SU: 0,
  TH: 4,
  TU: 2,
  WE: 3,
};

const MAX_OCCURRENCES = 500;

const parseRule = (rule: string): ParsedRule => {
  const parts = rule
    .split(';')
    .map(part => part.trim())
    .filter(Boolean);

  return parts.reduce<ParsedRule>(
    (parsed, part) => {
      const [rawKey, rawValue] = part.split('=');
      const key = (rawKey ?? '').toUpperCase();
      const value = rawValue ?? '';

      switch (key) {
        case 'FREQ':
          return { ...parsed, freq: value.toUpperCase() as ParsedRule['freq'] };
        case 'INTERVAL':
          return { ...parsed, interval: Math.max(1, Number(value) || 1) };
        case 'COUNT':
          return { ...parsed, count: Number(value) || null };
        case 'UNTIL':
          return { ...parsed, until: toDate(value) };
        case 'BYDAY':
          return {
            ...parsed,
            byDay: value
              .split(',')
              .map(code => DAY_CODES[code.trim().toUpperCase().slice(-2)])
              .filter(day => day !== undefined),
          };
        default:
          return parsed;
      }
    },
    { byDay: [], count: null, freq: null, interval: 1, until: null }
  );
};

const shift = (date: Date, freq: ParsedRule['freq'], interval: number): Date => {
  switch (freq) {
    case 'WEEKLY':
      return addWeeks(date, interval);
    case 'MONTHLY':
      return addMonths(date, interval);
    case 'YEARLY':
      return addYears(date, interval);
    default:
      return addDays(date, interval);
  }
};

const withSameTime = (day: Date, reference: Date): Date => {
  const result = new Date(day);
  result.setHours(reference.getHours(), reference.getMinutes(), reference.getSeconds(), reference.getMilliseconds());
  return result;
};

export const expandRecurrence = (
  start: Date,
  end: Date,
  recurrenceRule: string | undefined | null,
  view: CamDateRange
): CamDateRange[] => {
  const duration = end.getTime() - start.getTime();

  if (!recurrenceRule) {
    return overlaps(start, end, view.startDate, view.endDate) ? [{ endDate: end, startDate: start }] : [];
  }

  const rule = parseRule(recurrenceRule);
  if (!rule.freq) {
    return overlaps(start, end, view.startDate, view.endDate) ? [{ endDate: end, startDate: start }] : [];
  }

  const occurrences: CamDateRange[] = [];
  let cursor = start;
  let emitted = 0;

  for (let index = 0; index < MAX_OCCURRENCES; index++) {
    if (rule.until && cursor.getTime() > rule.until.getTime()) {
      break;
    }
    if (rule.count !== null && emitted >= rule.count) {
      break;
    }
    if (cursor.getTime() >= view.endDate.getTime()) {
      break;
    }

    const candidates =
      rule.freq === 'WEEKLY' && rule.byDay.length
        ? rule.byDay.map(day => {
            const weekStart = addDays(startOfDay(cursor), -((cursor.getDay() + 6) % 7));
            return withSameTime(addDays(weekStart, (day + 6) % 7), start);
          })
        : [cursor];

    candidates.forEach(candidate => {
      const candidateEnd = new Date(candidate.getTime() + duration);
      if (candidate.getTime() < start.getTime()) {
        return;
      }
      emitted++;
      if (overlaps(candidate, candidateEnd, view.startDate, view.endDate)) {
        occurrences.push({ endDate: candidateEnd, startDate: candidate });
      }
    });

    cursor = shift(cursor, rule.freq, rule.interval);
  }

  return occurrences;
};
